// R3 — DocumentPane. Renders a `DocumentModel` as its own scroll container and
// DOM subtree, which makes text selection naturally pane-isolated: a drag
// physically can't cross into the other pane, so no `user-select` hacks (SPEC
// "Sync anchors"). The blocks themselves come from the shared `DocumentBlocks`
// renderer, which also feeds the mobile interleaved column — this file owns only
// the scroll container, the measure, and the page frame around them.
//
// Font size is applied as the container's base `font-size` (px); headings and
// body use `em`-relative sizes so the per-pane slider scales the whole column.

import { type CSSProperties, type ReactNode, type Ref } from "react";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import type { PaneId } from "@bindery/shared/corpus/reader";
import { cn } from "@bindery/ui/lib/utils";
import { DocumentBlocks } from "./document-blocks";

interface DocumentPaneProps {
  model: DocumentModel;
  pane: PaneId;
  /** Base font size in px; the whole column scales from it (em-relative). */
  fontSize: number;
  /** Ref to the scroll container — the sync hooks read/drive its scrollTop. */
  ref?: Ref<HTMLDivElement>;
  className?: string;
  /**
   * Marks this pane as a keyboard gloss entry point: `role="region"`, an
   * "Original text" label pointed at the hidden `#gloss-kbd-hint`, and
   * `data-gloss-entry` for the word-navigation mode to find it by query.
   * Callers pass this only for the original pane, and only while glossing is
   * eligible for the book — the retelling pane must never be an entry point,
   * since its `data-start` offsets index a different model than the
   * original's. The scroll container is always a tab stop regardless (axe
   * `scrollable-region-focusable`); this prop only adds the extra region
   * semantics on top.
   */
  glossEntry?: boolean;
  /**
   * Rendered inside the article after the last block (page-turn nav). Carries
   * no `data-block-id`/`data-start`, so the sync hooks' block queries never see
   * it; callers should still omit it while scroll-sync is active (the reading
   * view only passes it in single-column mode).
   */
  footer?: ReactNode;
}

export function DocumentPane({
  model,
  pane,
  fontSize,
  ref,
  className,
  glossEntry = false,
  footer,
}: DocumentPaneProps) {
  return (
    <div
      ref={ref}
      data-pane={pane}
      tabIndex={0}
      role={glossEntry ? "region" : undefined}
      aria-label={glossEntry ? "Original text" : undefined}
      aria-describedby={glossEntry ? "gloss-kbd-hint" : undefined}
      data-gloss-entry={glossEntry ? "true" : undefined}
      className={cn(
        "h-full overflow-y-auto overscroll-contain focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60",
        className,
      )}
      style={{ fontSize: `${String(fontSize)}px` } satisfies CSSProperties}
    >
      <article
        className={cn(
          // flex-col (not the default block flow): flex items' margins never
          // collapse, so the hard-align hook's injected marginTop/marginBottom
          // land exactly as computed instead of merging with a sibling's mb-*.
          "mx-auto flex max-w-[42rem] flex-col px-6 pb-[60vh] pt-10 font-serif leading-[1.7]",
          // The original pane reads as a quieter reference column.
          pane === "retelling" ? "text-foreground" : "text-foreground/85",
        )}
      >
        <DocumentBlocks model={model} pane={pane} />
        {footer}
      </article>
    </div>
  );
}
